import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormControl, Validators, FormArray } from '@angular/forms';

import { AppConstants } from 'appshared/app-constants';
import * as Models from 'appshared/shared-models';
import { MiscTools } from 'appshared/misc-tools';
import { TextTools } from 'appshared/text-tools';
import { UITools } from 'appshared/ui-tools';
import { SharedLicenseTools } from 'appshared/shared-license-tools';
import { LicenseValidationTools } from 'appshared/license-validation-tools';

import { MyKeysService } from '../my-keys.service';
import { MySettingsService } from '../../my-settings/my-settings.service';
import { MyPackagesService } from '../../my-packages/my-packages.service';
import { UiAlertsService } from 'client/app/components/ui-alerts/ui-alerts.service';
import { AuthService } from 'client/app/services/auth.service';
import { LicensingAdminService } from '../../licensing-admin/licensing-admin.service';

import { TableKeysComponent } from 'client/app/components/shared/table-keys/table-keys.component';
import { TableBillingCodesComponent } from 'client/app/components/shared/table-billing-codes/table-billing-codes.component';
import { PopupBoxComponent } from 'client/app/components/shared/popup-box/popup-box.component';

@Component({
	selector: 'app-license-key-list',
	templateUrl: './license-key-list.component.html',
	styleUrls: ['./license-key-list.component.scss']
})
export class LicenseKeyListComponent implements OnInit, OnDestroy {
	ac = AppConstants;
	tt = TextTools;
	mt = MiscTools;

	@ViewChild('keysTable1') keysTable1: TableKeysComponent = null;
	@ViewChild('keysTable2') keysTable2: TableKeysComponent = null;
	@ViewChild('keysTable3') keysTable3: TableKeysComponent = null;
	@ViewChild('billingCodesTable') billingCodesTable: TableBillingCodesComponent = null;
	@ViewChild(PopupBoxComponent) popupBox: PopupBoxComponent = null;

	private userSubscription: Subscription;
	authUser: Models.AuthUser;

	packages: Models.Package[] = [];

	userKeys: Models.UserLicenseKey[] = [];
	deletedKeys: Models.UserLicenseKey[] = [];

	// activeKeys: Models.UserLicenseKey[] = [];
	// inactiveKeys: Models.UserLicenseKey[] = [];
	// deletedKeys: Models.UserLicenseKey[] = [];

	activeKeysToShow: Models.LPActivation[] = [];
	inactiveKeysToShow: Models.LPActivation[] = [];
	deletedKeysToShow: Models.LPActivation[] = [];

	hostids: Models.UserHostId[] = [];

	keyWarnings = {};
	keyExpiries = {};

	keyActiveHostCounts = {};
	recentHostIconPopup = '# Host IDs eporting non-zero traffic over the last ' + AppConstants.recentUsedHostsDays + ' days';

	availableSelfServicePackageTemplates: Models.PackageKeyTemplate[] = [];

	orgUsers: Models.User[] = [];

	loading = true;
	showKeyCreator = false;

	// showBxProtocolStats = false;
	showBxProtocolStats = true;

	keyProducts: Models.LicenseProduct[] = [];

	editMode: boolean = false;
	editNamesForm: UntypedFormGroup;

	userSnoozes: Models.UserLicenseKeySnooze[] = [];

	billingCodeOrgs: Models.OrganizationBillingCodes[] = [];
	showBillingCodeTab: boolean = false;
	hideBillingAuths: boolean = true;

	billingCodeToReset: Models.BillingCode = null;
	billingCodes: Models.BillingCode[] = [];

	updateBillingCodeForm: UntypedFormGroup;
	updateBillingCodeErrors: string[] = [];
	billingCodeToUpdate: Models.BillingCode = null;

	existingActivePackageKeys: string[] = [];
	existingDeletedPackageKeys: string[] = [];

	saving: boolean = false;
	addKeysErrors: string[] = [];
	addKeysForm: UntypedFormGroup;


	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private authService: AuthService,
		private myKeysService: MyKeysService,
		private mySettingsService: MySettingsService,
		private myPackagesService: MyPackagesService,
		private licensingAdminService: LicensingAdminService,
		private uiAlertsService: UiAlertsService
	) { }

	ngOnInit(): void {
		this.userSubscription = this.authService.user.subscribe((authUser) => {
			this.authUser = authUser;

			if (authUser) {
				this.loadTables();
			}
		});
	}

	ngOnDestroy() {
		if (this.userSubscription) this.userSubscription.unsubscribe();
	}

	async loadTables(forceRefresh = false) {
		this.loading = true;

		UITools.clickButton('nav-active-keys-tab');

		this.userKeys = [];
		this.deletedKeys = [];
		this.availableSelfServicePackageTemplates = [];
		this.activeKeysToShow = [];
		this.inactiveKeysToShow = [];
		this.deletedKeysToShow = [];

		// this.deletedKeys = [];
		// this.keysToShow = [];

		this.packages = await this.myPackagesService.getMyPackages(forceRefresh);
		this.keyProducts = await this.licensingAdminService.getProducts();

		this.userKeys = await this.myKeysService.getUserKeys(false, forceRefresh);
		this.deletedKeys = await this.myKeysService.getUserKeys(true, forceRefresh);

		this.keyActiveHostCounts = await this.myKeysService.getActiveHostCounts();

		this.userSnoozes = await this.myKeysService.getUserSnoozes();
		this.userSnoozes.sort((a, b) => (a.expires_at > b.expires_at) ? 1 : -1);

		await this.getBillingCodes();

		this.hostids = await this.myKeysService.getUserHostids(forceRefresh);

		// this.showBxProtocolStats = TextTools.getUserPropValue(this.authUser, AppConstants.showBxProtocolStatsOverride) === 'yes';

		for (const userKey of this.userKeys) {
			let item: Models.LPActivation = userKey.activation;

			item['__niceProduct'] = this.niceKeyProduct(item.product);
			// item['__popover'] = PopOverTools.getKeyPopoverLines(item, this.keyProducts, [], [], false).join('\n');

			item['__user_id'] = userKey.id;
			item['__user_label'] = this.getLabel(userKey);
			item['__key_label'] = (userKey.activation && userKey.activation.ext_label) ? userKey.activation.ext_label : '';
			item['__raw_label'] = userKey.label;

			if (this.keyActiveHostCounts[item.id])
				item['__active_hostids'] = this.keyActiveHostCounts[item.id];
			else
				item['__active_hostids'] = '';

			const exp = SharedLicenseTools.getKeyExpiration(item, '', true);
			if ((item.enabled === 0 && !SharedLicenseTools.isSpecialKey(item)) || (exp != null && MiscTools.hasExpired(exp)))
				this.inactiveKeysToShow.push(item);
			else
				this.activeKeysToShow.push(item);
		} // for

		for (const userKey of this.deletedKeys) {
			let item: Models.LPActivation = userKey.activation;

			item['__niceProduct'] = this.niceKeyProduct(item.product);
			// item['__popover'] = PopOverTools.getKeyPopoverLines(item, this.keyProducts, [], [], false).join('\n');

			item['__user_id'] = userKey.id;
			item['__user_label'] = this.getLabel(userKey);
			item['__key_label'] = (userKey.activation && userKey.activation.ext_label) ? userKey.activation.ext_label : '';
			item['__raw_label'] = userKey.label;

			if (this.keyActiveHostCounts[item.id])
				item['__active_hostids'] = this.keyActiveHostCounts[item.id];
			else
				item['__active_hostids'] = '';

			this.deletedKeysToShow.push(item);
		} // for

		this.updateKeyTables();

		this.workoutAvailableSelfServiceKeys();

		this.orgUsers = await this.mySettingsService.getOrgUsers();

		const formControls: any = {};
		for (const item of this.activeKeysToShow)
			formControls['label_' + item['__user_id']] = new UntypedFormControl(item['__raw_label'], [])
		this.editNamesForm = new UntypedFormGroup(formControls);

		this.loading = false;
	}

	// --------------------------------------------------------------------
	async getBillingCodes() {
		this.billingCodeOrgs = await this.myKeysService.getBillingCodesOrganizations();
		this.showBillingCodeTab = this.billingCodeOrgs.length > 0;

		this.billingCodes = [];
		if (this.billingCodeOrgs) {
			for (const item of this.billingCodeOrgs)
				if (item.billingCodes && this.billingCodes.length === 0)
					this.billingCodes = item.billingCodes;

			if (this.billingCodes.length > 0) {
				await MiscTools.delay(100);
				if (this.billingCodesTable) {
					this.billingCodesTable.updateContent(this.billingCodes, 'WIPE-FILTER.cp-my-keys-b-codes-' + this.authUser.id, {});
				}
			} // if
		} // if
	}

	// --------------------------------------------------------------------
	updateKeyTables() {
		if (this.keysTable1) {
			this.keysTable1.updateContent(this.activeKeysToShow, 'WIPE-FILTER.cp-my-keys-active',
				{
					staffMode: false,
					addPopovers: true,

					showStaffDelete: false,
					showEnabled: true,
					showUserLabel: true,
					showInfo: false,
					showOrganization: false,
					showFullProduct: true,
					showFullType: true,
					showExpandedActivations: true,
					showNumUsers: false,
					showActiveCount: true,
					showMeterIcon: true,
					showProtocolIcon: true,
					showSnoozed: false,
					showLastTouched: false,
					showSalesEngineer: false,
					showCommercialType: false,
					showMeterIrregularities: false
				});
		} // if

		if (this.keysTable2) {
			this.keysTable2.updateContent(this.inactiveKeysToShow, 'WIPE-FILTER.cp-my-keys-old',
				{
					staffMode: false,
					addPopovers: true,

					showStaffDelete: false,
					showEnabled: true,
					showUserLabel: true,
					showInfo: false,
					showOrganization: false,
					showFullProduct: true,
					showFullType: true,
					showExpandedActivations: true,
					showNumUsers: false,
					showActiveCount: true,
					showMeterIcon: true,
					showProtocolIcon: true,
					showSnoozed: false,
					showLastTouched: false,
					showSalesEngineer: false,
					showCommercialType: false,
					showMeterIrregularities: false
				});
		} // if

		if (this.keysTable3) {
			this.keysTable3.updateContent(this.deletedKeysToShow, 'WIPE-FILTER.cp-my-keys-deleted',
				{
					staffMode: false,
					addPopovers: true,

					showStaffDelete: false,
					showEnabled: true,
					showUserLabel: true,
					showInfo: false,
					showOrganization: false,
					showFullProduct: true,
					showFullType: true,
					showExpandedActivations: true,
					showNumUsers: false,
					showActiveCount: true,
					showMeterIcon: true,
					showProtocolIcon: true,
					showSnoozed: false,
					showLastTouched: false,
					showSalesEngineer: false,
					showCommercialType: false,
					showMeterIrregularities: false
				});
		} // if
	}

	// --------------------------------------------------------------------
	async workoutAvailableSelfServiceKeys() {
		// work out what keys they can generate...
		this.availableSelfServicePackageTemplates = [];
		for (const pkg of this.packages) {
			if (pkg.is_enabled === 1) {
				for (const pkgTemplate of pkg.key_templates) {
					let foundKey = false;
					// for (const userKey of this.userKeys)
					// 	if (userKey.activation && userKey.activation.zcp_template_id === pkgTemplate.template_id)
					// 		foundKey = true;

					// for (const userKey of this.deletedKeys)
					// 	if (userKey.activation && userKey.activation.zcp_template_id === pkgTemplate.template_id)
					// 		foundKey = true;

					if (!foundKey) this.availableSelfServicePackageTemplates.push(pkgTemplate);
				} // for
			} // if
		} //for
	}

	// --------------------------------------------------------------------
	async generateKeyViaTemplate(packageID: number, templateID: number) {
		this.existingActivePackageKeys = [];
		this.existingDeletedPackageKeys = [];
		for (const pkg of this.packages) {
			if (pkg.id === packageID) {
				for (const pkgTemplate of pkg.key_templates) {
					for (const userKey of this.userKeys)
						if (userKey.activation && userKey.activation.zcp_template_id === pkgTemplate.template_id)
							this.existingActivePackageKeys.push(userKey.activation.key);

					for (const userKey of this.deletedKeys)
						if (userKey.activation && userKey.activation.zcp_template_id === pkgTemplate.template_id)
							this.existingDeletedPackageKeys.push(userKey.activation.key);
				} // for
			} // if
		} //for

		if (this.existingActivePackageKeys.length > 0 || this.existingDeletedPackageKeys.length > 0) {
			UITools.clickButton('open-key-already-modal');
			return;
		} // if

		try {
			this.loading = true;
			const ret = await this.myKeysService.generateKeyViaTemplate(packageID, templateID);
			if (ret) {
				this.uiAlertsService.addMsg('Your new key is available.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
				this.router.navigate(['/' + AppConstants.urls.mykeys, ret.id]);
			} else {
				this.loading = false;
				this.uiAlertsService.addMsg('There was an error adding your key.', 'error');
			}
		} catch (e: any) {
			this.loading = false;
			this.uiAlertsService.addMsg(e.message, 'error');
		}
	}

	// --------------------------------------------------------------------
	niceKeyProduct(product: string) {
		const idx = MiscTools.findIndexGeneric(this.keyProducts, 'name', product);
		if (idx === -1)
			return product;
		else
			return this.keyProducts[idx].basic_label;
	}

	// --------------------------------------------------------------------
	getPackage(id: number) {
		const idx = MiscTools.findIndex(this.packages, id);
		if (idx !== -1) return this.packages[idx];
		return null;
	}

	// --------------------------------------------------------------------
	getKey(id: number) {
		const idx = MiscTools.findIndex(this.userKeys, id);
		if (idx !== -1) {
			return this.userKeys[idx].activation.key;
		} else {
			const idx2 = MiscTools.findIndex(this.deletedKeys, id);
			if (idx2 !== -1)
				return this.userKeys[idx].activation.key;
		}
		return '';
	}

	// --------------------------------------------------------------------
	getLabelFromId(id: number) {
		const idx = MiscTools.findIndex(this.userKeys, id);
		if (idx !== -1) {
			return this.getLabel(this.userKeys[idx]);
		} else {
			const idx2 = MiscTools.findIndex(this.deletedKeys, id);
			if (idx2 !== -1)
				return this.getLabel(this.userKeys[idx2]);;
		}
		return '';
	}

	// --------------------------------------------------------------------
	getLabel(userKey: Models.UserLicenseKey) {
		if (userKey.label && userKey.label !== '')
			return userKey.label;
		else
			return 'Not Set';
	}

	// // --------------------------------------------------------------------
	// getLabelMode(userKey: Models.UserLicenseKey) {
	// 	if (userKey.label && userKey.label !== '')
	// 		return 'user-label';
	// 	else if (userKey.activation && userKey.activation.ext_label && userKey.activation.ext_label !== '')
	// 		return 'key-label';
	// 	else
	// 		return 'no-label';
	// }

	// --------------------------------------------------------------------
	handleClick(id: number) {
		this.router.navigate([id], { relativeTo: this.route });
	}

	// --------------------------------------------------------------------
	async emailUserKeysReport() {
		await this.myKeysService.emailUserKeysReport();
	}

	// --------------------------------------------------------------------
	openUserKeysReport(metricType: string) {
		let url = AppConstants.apiUrl + AppConstants.apiUrls.mykeys + '/report'
			+ '?metricType=' + encodeURIComponent(metricType);
		window.open(url, '_blank');
	}

	// --------------------------------------------------------------------
	async emptyTrash() {
		if (!confirm('Are you sure you want to purge these ' + this.deletedKeysToShow.length + ' key(s)? - You will NOT be able to recover them.'))
			return;

		for (const deletedKey of this.deletedKeys)
			await this.myKeysService.purgeKey(deletedKey, false);

		this.deletedKeys = await this.myKeysService.getUserKeys(true, true);
		this.deletedKeysToShow = [];

		this.uiAlertsService.addMsg('The trash has been emptied.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

		this.updateKeyTables();

		if (document.getElementById('nav-active-keys-tab'))
			document.getElementById('nav-active-keys-tab').click();

	}

	// --------------------------------------------------------------------
	async saveLabels() {
		this.loading = true;
		this.editMode = false;

		const updatedIdx: number[] = [];
		let counter = 0;
		for (const item of this.activeKeysToShow) {
			const newLabel = this.editNamesForm.value['label_' + item['__user_id']].trim();
			if (newLabel !== item['__raw_label']) {
				const userKey: Models.UserLicenseKey = MiscTools.pickItem(this.userKeys, 'id', +item['__user_id']);
				if (userKey) {
					userKey.label = newLabel;
					await this.myKeysService.updateKey(userKey, false);
					counter++;
				} // if
			} // if
		} // for

		if (counter > 0) {
			this.uiAlertsService.addMsg('Your changes have been saved.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			// this.userKeys = await this.myKeysService.getUserKeys(false, true);
			await this.loadTables(true);
		} // if

		this.loading = false;
	}

	// *********************************************************
	async resetBillCodeAuthCodePrep(billingCode: Models.BillingCode) {
		if (!billingCode) return;
		this.billingCodeToReset = MiscTools.deepClone(billingCode);

		this.popupBox.openPopup('confirm-text', 'resetBillCodeAuthCode', [], 'Reset this Billing Code\'s AUTHORIZATION CODE',
			'If you reset this billing code, a new randomly generated AUTHORIZATION CODE will be generated.\n'
			+ 'If you proceed, any ZIXI systems that use this billing code with the current AUTHORIZATION CODE will not be able to report traffic again until you update it to use the new AUTHORIZATION CODE.',
			null,
			{ confirmButtonText: 'Reset this Billing Code\'s AUTHORIZATION CODE', rejectButtonText: 'Cancel', confirmText: 'reset' });
	}

	// *********************************************************
	async resetBillCodeAuthCode() {
		if (!this.billingCodeToReset) return;

		const result = await this.myKeysService.resetBillCodeAuthCode(this.billingCodeToReset);
		if (result) {
			this.uiAlertsService.addMsg(this.billingCodeToReset.billing_code + '\'s authorization code has been reset.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

			this.billingCodeToReset = null;
			this.loading = false;

			UITools.clickButton('nav-active-keys-tab');
			await this.getBillingCodes();
			await MiscTools.delay(100);
			UITools.clickButton('nav-billing-codes-tab');

		} else {
			return false;
		}
	}

	// *********************************************************
	async updateBillingCodePrep(billingCode: Models.BillingCode) {
		if (!billingCode) return;
		this.updateBillingCodeErrors = [];
		this.updateBillingCodeForm = new UntypedFormGroup({
			label: new UntypedFormControl(billingCode.label, [Validators.required])
		});
		this.billingCodeToUpdate = MiscTools.deepClone(billingCode);
		UITools.clickButton('open-update-billing-code-modal');
	}

	// *********************************************************
	async updateBillingCode() {
		this.loading = true;
		this.updateBillingCodeErrors = [];
		this.billingCodeToUpdate.label = this.updateBillingCodeForm.value.label;
		if (!this.billingCodeToUpdate.label || this.billingCodeToUpdate.label === '')
			this.updateBillingCodeErrors.push('You must provide a Name/Label');

		if (this.updateBillingCodeErrors.length > 0) {
			this.loading = false;
			return;
		}

		try {
			const returnedBC: Models.BillingCode = await this.myKeysService.updateBillingCode(this.billingCodeToUpdate);

			this.uiAlertsService.addMsg(returnedBC.billing_code + '\'s label has been updated.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

			console.log(returnedBC);

			this.loading = false;
			this.updateBillingCodeErrors = [];
			this.billingCodeToUpdate = null;
			UITools.clickButton('close-update-billing-code-modal');

			UITools.clickButton('nav-active-keys-tab');
			await this.getBillingCodes();
			await MiscTools.delay(100);
			UITools.clickButton('nav-billing-codes-tab');

		} catch (ex: any) {
			this.updateBillingCodeErrors.push(ex.toString());
			this.loading = false;
		}
	}

	// *********************************************************
	async openAddKeysForm() {
		this.addKeysErrors = [];
		if (!this.addKeysForm) {
			this.addKeysForm = new UntypedFormGroup({
				keyList: new UntypedFormControl(null, [Validators.required]),
			});
		}

		UITools.clickButton('open-add-keys-modal');
	}

	// *********************************************************
	async addKeys() {
		this.addKeysErrors = [];

		const keys = this.addKeysForm.value.keyList;

		const registerErrors = [];

		let numProcessed = 0;
		let numAdded = 0;
		let numUpdated = 0;
		let numSkipped = 0;
		const array = keys.match(/[^\r\n]+/g);

		const warnings = [];

		for (const key of array) {
			if (key.trim() !== '') {
				if (numProcessed >= AppConstants.maxKeysToRegisterPerSubmit) {
					numSkipped++;
				} else {
					numProcessed++;

					let justKey = '';
					let justLabel = '';
					if (key.includes(':')) {
						justKey = TextTools.getLabel(key.trim()); // everything before the :
						justLabel = TextTools.getValue(key.trim()); // everything after the :
					} else if (key.includes(' ')) {
						justKey = TextTools.getLabel(key.trim(), ' '); // everything before the space
						justLabel = TextTools.getValue(key.trim(), ' '); // everything after the space
					} else {
						justKey = key.trim();
					}

					try {
						const idx = MiscTools.findIndexGeneric(this.userKeys, 'activation_key', justKey);
						if (idx !== -1) {
							const theKey = this.userKeys[idx];
							if (justLabel === '' || theKey.label === justLabel) {
								if (justLabel === '')
									warnings.push(justKey + ' has already been registered.');
								else
									warnings.push(justKey + ' has already been registered with that label.');
							} else {
								numUpdated++;
								theKey.label = justLabel;
								await this.myKeysService.updateKey(theKey, false);
							}

						} else {
							// console.log('try to add ' + key.trim());
							const userKeyObj = await this.myKeysService.registerKey(justKey, justLabel, false);
							this.userKeys.push(userKeyObj);
							numAdded++;
						}

					} catch (e: any) {
						registerErrors.push(e.message);
					}
				}
			}
		}

		if (warnings.length !== 0)
			this.uiAlertsService.addMsg(warnings.join('\n'), 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

		if (registerErrors.length > 0)
			this.uiAlertsService.addMsg(registerErrors.join('\n'), 'error');

		if (numAdded === 1)
			this.uiAlertsService.addMsg('Registered one key.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		else if (numAdded > 1)
			this.uiAlertsService.addMsg('Registered ' + numAdded + ' keys.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

		if (numUpdated === 1)
			this.uiAlertsService.addMsg('Updated label for one key.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		else if (numUpdated > 1)
			this.uiAlertsService.addMsg('Updated label for ' + numUpdated + ' keys.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

		if (numSkipped === 1)
			this.uiAlertsService.addMsg('Ignoring one key. Maximum # of keys reached.', 'warning');
		else if (numSkipped > 1)
			this.uiAlertsService.addMsg('Ignoring ' + numSkipped + ' keys. Maximum # of keys reached.', 'warning');

		this.saving = false;

		UITools.clickButton('close-add-keys-modal');
		this.addKeysForm = null;
		await this.loadTables(true);
	}

	// --------------------------------------------------------------------
	openBillingCodeReport(orgId: number) {
		let url = AppConstants.apiUrl + AppConstants.apiUrls.mykeys + '/org-billing-codes/' + orgId + '/report';
		window.open(url, '_blank');
	}

	// --------------------------------------------------------------------
	copyToClipboardAlert(item: string = '') {
		this.uiAlertsService.copyToClipboardAlert(item);
	}

	// ------------------------------------------------------------------------
	// Back and Forth with components
	// ------------------------------------------------------------------------
	getParentMethod(): any {
		return {
			resetBillCodeAuthCodePrep: async (billingCode: Models.BillingCode) => {
				this.resetBillCodeAuthCodePrep(billingCode);
			},
			updateBillingCodePrep: async (billingCode: Models.BillingCode) => {
				this.updateBillingCodePrep(billingCode);
			},
			disableBillingCode: async (billingCode: Models.BillingCode) => {
			},
			enableBillingCode: async (billingCode: Models.BillingCode) => {
			},
			popupCallBack: async (callBack: string, args: any) => {
				if (callBack === 'resetBillCodeAuthCode' && args.length === 0)
					this.resetBillCodeAuthCode();
				else if (callBack === 'XXXXX' && args.length === 1)
					console.log('args=' + args);

				else
					this.uiAlertsService.addMsg('Unknown callBack (' + callBack + ') or bad number of arguments (' + args.length + ').', 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			}
		}
	} // getParentMethod

}
